import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (src) => (src.startsWith("http") ? src : `${BACKEND_URL}${src}`);

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    toast.success("Foto's toegevoegd");
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Deze foto verwijderen uit de galerij?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Fotogalerij</h1>
          <p className="text-brand-muted mt-1">Deze foto's verschijnen in de galerij op je website.</p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="gallery-upload-button">
          {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Foto's uploaden
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="gallery-file-input" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-pink" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="text-center bg-white rounded-3xl border border-brand-border p-12 mt-8">
          <ImageIcon className="mx-auto text-brand-pink mb-3" size={32} />
          <p className="text-brand-charcoal/80">Nog geen foto's. Upload je mooiste nagelcreaties.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-8">
          {items.map((g) => (
            <div key={g.id} className="relative aspect-square rounded-2xl overflow-hidden border border-brand-border group" data-testid={`gallery-admin-${g.id}`}>
              <img src={resolveSrc(g.src)} alt={g.caption || "Nagelcreatie"} loading="lazy" className="w-full h-full object-cover" />
              <button onClick={() => remove(g.id)} className="absolute top-2 right-2 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm" data-testid={`gallery-delete-${g.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
